/**
 * Brand identity tokens for the onboarding section.
 * Source of truth: agentiza-identidad-visual.md (brand book operational summary).
 */

export const brandName = 'agentiza';

export const onboardingColors = {
  orange: '#FF7235', // Primario · Acción — CTAs, números clave, pulso visual
  inkNavy: '#040433', // Primario · Texto — texto, navs, autoridad
  signalPurple: '#5D3CBF', // Secundario · IA — soporte, nunca protagonista junto al naranja
  paper: '#FAF7F2', // Neutro · Fondo
  paper2: '#F2EDE3', // Neutro · Sección
  pureWhite: '#FFFFFF',
  fog: '#7777A0', // Metadata
  ink800: '#1A1A4A', // Profundo
  state: {
    success: '#1F8F5E',
    warning: '#D99400',
    error: '#D9342B',
    info: '#2B6DD9',
  },
} as const;

/** Regla 60/30/10: proporción objetivo de superficie por color. */
export const onboardingColorDistribution = {
  neutral: 0.6, // Paper o White
  inkNavy: 0.3, // texto, navs, autoridad
  orange: 0.1, // CTAs, números clave, pulso visual
} as const;

export const onboardingColorCombinations = {
  allowed: [
    'ink-sobre-paper',
    'orange-sobre-ink',
    'white-sobre-orange',
  ],
  forbidden: [
    'ink-sobre-orange', // zumbido visual
    'orange-y-purple-como-protagonistas', // saturación — purple es solo soporte
    'orange-sobre-tonos-naranja-claros', // falla WCAG
  ],
} as const;

export const onboardingTypographyFamilies = {
  display: {
    family: 'Orbitron', weights: '400-700', usage: 'Headlines >=28px, wordmark. Nunca en párrafos.',
  },
  ui: {
    family: 'Figtree', weights: '300-900', usage: 'Texto UI, body, labels. Legible desde 12px.',
  },
  fallback: {
    family: 'DM Sans', weights: '100-900', usage: 'Email / sistemas legacy sin Figtree.',
  },
  mono: {
    family: 'JetBrains Mono', weights: '400-600', usage: 'Metadata, specs, etiquetas, overlines.',
  },
} as const;

/**
 * Niveles de escala tipográfica relevantes para UI de formulario.
 * Se omiten DISPLAY/H1/H2, propios de landing/hero.
 */
export const onboardingTypeScale = [
  {
    level: 'H3',
    family: 'Figtree',
    size: 26,
    weight: 600,
    lineHeight: 1.20,
    tracking: '-0.005em',
    usage: 'Cards, módulos',
  },
  {
    level: 'H4',
    family: 'Figtree',
    size: 20,
    weight: 600,
    lineHeight: 1.30,
    tracking: '0',
    usage: 'Títulos secundarios, features',
  },
  {
    level: 'BODY_L',
    family: 'Figtree',
    size: 18,
    weight: 400,
    lineHeight: 1.55,
    tracking: '0',
    usage: 'Lede, intros',
  },
  {
    level: 'BODY',
    family: 'Figtree',
    size: 15,
    weight: 400,
    lineHeight: 1.60,
    tracking: '0',
    usage: 'Párrafo default, UI',
  },
  {
    level: 'BODY_S',
    family: 'Figtree',
    size: 13,
    weight: 400,
    lineHeight: 1.55,
    tracking: '0',
    usage: 'Descriptores, helper text',
  },
  {
    level: 'CAPTION',
    family: 'Figtree',
    size: 12,
    weight: 500,
    lineHeight: 1.40,
    tracking: '0.01em',
    usage: 'Metadata, timestamps',
  },
  {
    level: 'LABEL',
    family: 'Figtree',
    size: 11,
    weight: 600,
    lineHeight: 1.30,
    tracking: '0.05em',
    usage: 'Etiquetas, inputs, forms',
  },
  {
    level: 'OVERLINE',
    family: 'Figtree',
    size: 10,
    weight: 600,
    lineHeight: 1.30,
    tracking: '0.18em',
    usage: 'Eyebrow, kickers',
  },
] as const;

type TypeScaleLevel = typeof onboardingTypeScale[number]['level'];

/** sx-ready style per type-scale level, e.g. `sx={onboardingTypeSx.CAPTION}`. */
export const onboardingTypeSx: Record<TypeScaleLevel, {
  fontFamily: string;
  fontSize: string;
  fontWeight: number;
  lineHeight: number;
  letterSpacing: string;
}> = Object.fromEntries(onboardingTypeScale.map((level) => [level.level, {
  fontFamily: `'${level.family}', 'DM Sans', sans-serif`,
  fontSize: `${level.size}px`,
  fontWeight: level.weight,
  lineHeight: level.lineHeight,
  letterSpacing: level.tracking,
}])) as Record<TypeScaleLevel, {
  fontFamily: string;
  fontSize: string;
  fontWeight: number;
  lineHeight: number;
  letterSpacing: string;
}>;

/** Voz y tono (es-MX), acotado al contexto de onboarding. */
export const onboardingVoice = {
  archetype: 'Sabio + Aliado',
  contextMood: 'Guía calma',
  contextExample: 'Listo, armemos tu primer agente. Tarda menos que un café.',
  writingRules: [
    'Abrir con la ganancia del cliente, no con la feature.',
    'Sujeto + verbo + objeto. Si se puede cortar, se corta.',
    'Números antes que adjetivos: "78% resuelto" > "muy eficiente".',
    'Tuteo neutro mexicano siempre (tú / tu / tus). Nunca voseo.',
    'Oraciones de 20 palabras o menos.',
    'Cero signos de admiración salvo celebración de hito real.',
  ],
  vocabulary: {
    prefer: [
      'agente', 'cargar conocimiento', 'desplegar', 'derivar a un humano', 'modo privado', 'intención',
    ],
    avoid: ['bot', 'entrenar LLM', 'launch / lanzar', 'escalation', 'sandbox', 'intent'],
  },
  forbiddenWords: [
    'revolucionario', 'game-changer', 'potenciar', 'solución integral',
    'próxima generación', 'de vanguardia', 'disruptivo', 'sinergia',
    'partnership', 'stakeholder', 'empoderar', 'transformación digital',
    'estimado cliente', 'llevar al siguiente nivel', 'la IA del futuro',
  ],
  forbiddenEmojis: ['🚀', '🔥', '💯', '✨', '🎉', '💪', '🙌', '⚡'],
  cta: {
    rule: '2-4 palabras, siempre en imperativo.',
    examples: ['Crear mi agente', 'Agendar demo', 'Ver caso', 'Empezar gratis'],
    antiExamples: ['Descubre el futuro de la IA', 'Revoluciona tu negocio ahora mismo'],
  },
} as const;
